/**
 * IFRAP-AI.fr - Page Veille Stratégique
 * Agrégation des flux médias et think tanks
 */

import React, { useState, useEffect, useCallback } from 'react';
import api from '../../api/client';
import { RefreshCw, ExternalLink, Globe, Building2, Newspaper, Filter, Clock } from 'lucide-react';

const CATEGORIES = {
  all: { label: 'Tout', icon: Globe },
  think_tanks: { label: 'Think Tanks', icon: Building2 },
  media: { label: 'Médias', icon: Newspaper },
};

const REGIONS = {
  all: 'Tous',
  fr: 'France',
  eu: 'Europe',
  us: 'États-Unis',
  intl: 'International',
};

const VeillePage = () => {
  const [activeTab, setActiveTab] = useState('all');
  const [region, setRegion] = useState('all');
  const [items, setItems] = useState([]);
  const [sources, setSources] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);

  const fetchFeed = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      let endpoint = '/api/veille/feed';
      let params = { limit: 100 };

      if (activeTab === 'think_tanks') {
        endpoint = '/api/veille/think-tanks';
        if (region !== 'all' && region !== 'intl') {
          params.region = region;
        }
      } else if (activeTab === 'media') {
        endpoint = '/api/veille/media';
        if (region === 'fr') params.region = 'fr';
        else if (region === 'intl' || region === 'eu' || region === 'us') params.region = 'intl';
      }

      const response = await api.get(endpoint, { params });
      setItems(response.data.items || []);
      setLastUpdate(new Date());
    } catch (err) {
      console.error('Error fetching veille:', err);
      setError('Erreur lors du chargement de la veille');
    } finally {
      setLoading(false);
    }
  }, [activeTab, region]);

  const fetchSources = async () => {
    try {
      const response = await api.get('/api/veille/sources');
      setSources(response.data);
    } catch (err) {
      console.error('Error fetching sources:', err);
    }
  };

  useEffect(() => {
    fetchFeed();
    fetchSources();
  }, [fetchFeed]);

  // Auto-refresh every 5 minutes
  useEffect(() => {
    const interval = setInterval(fetchFeed, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchFeed]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) return `Il y a ${diffMins} min`;
    if (diffHours < 24) return `Il y a ${diffHours}h`;
    if (diffDays < 7) return `Il y a ${diffDays}j`;
    return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
  };

  const getSourceColor = (category) => {
    const colors = {
      think_tank_fr: 'bg-blue-100 text-blue-800',
      think_tank_eu: 'bg-purple-100 text-purple-800',
      think_tank_us: 'bg-red-100 text-red-800',
      media_fr: 'bg-green-100 text-green-800',
      media_intl: 'bg-orange-100 text-orange-800',
      institution: 'bg-gray-100 text-gray-800',
    };
    return colors[category] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#000091] to-[#1a1a6e] text-white py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold">Veille Stratégique</h1>
              <p className="text-blue-200 mt-1">
                {sources ? `${sources.active_sources} sources` : 'Chargement...'} • Médias & Think Tanks
              </p>
            </div>
            <button
              onClick={fetchFeed}
              disabled={loading}
              className="flex items-center gap-2 bg-white/10 hover:bg-white/20 px-4 py-2 rounded-lg transition-colors"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Actualiser
            </button>
          </div>

          {/* Tabs */}
          <div className="flex gap-2 mt-6">
            {Object.entries(CATEGORIES).map(([key, { label, icon: Icon }]) => (
              <button
                key={key}
                onClick={() => setActiveTab(key)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                  activeTab === key
                    ? 'bg-white text-[#000091]'
                    : 'bg-white/10 hover:bg-white/20 text-white'
                }`}
              >
                <Icon className="w-4 h-4" />
                {label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border-b border-gray-200 py-3">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Filter className="w-4 h-4 text-gray-400" />
              <div className="flex gap-2">
                {Object.entries(REGIONS).map(([key, label]) => (
                  <button
                    key={key}
                    onClick={() => setRegion(key)}
                    className={`px-3 py-1 text-sm rounded-full transition-colors ${
                      region === key
                        ? 'bg-[#000091] text-white'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
            {lastUpdate && (
              <div className="flex items-center gap-2 text-xs text-gray-500">
                <Clock className="w-3 h-3" />
                Mis à jour : {lastUpdate.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {error && items.length === 0 && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
            {error}
          </div>
        )}

        {loading && items.length === 0 ? (
          <div className="flex items-center justify-center py-12">
            <RefreshCw className="w-8 h-8 text-[#000091] animate-spin" />
          </div>
        ) : (
          <div className="space-y-3">
            {items.map((item, index) => (
              <a
                key={item.id || index}
                href={item.link}
                target="_blank"
                rel="noopener noreferrer"
                className="block bg-white rounded-lg border border-gray-200 p-4 hover:border-[#000091] hover:shadow-md transition-all group"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2">
                      <span className={`px-2 py-0.5 text-xs font-medium rounded ${getSourceColor(item.source_category)}`}>
                        {item.source_short || item.source_name}
                      </span>
                      {item.language === 'en' && (
                        <span className="px-1.5 py-0.5 text-[10px] bg-gray-100 text-gray-500 rounded">
                          EN
                        </span>
                      )}
                      <span className="text-xs text-gray-400">
                        {formatDate(item.published)}
                      </span>
                    </div>
                    <h3 className="font-medium text-gray-900 group-hover:text-[#000091] line-clamp-2">
                      {item.title}
                    </h3>
                    {item.summary && (
                      <p className="text-sm text-gray-500 mt-1 line-clamp-2">
                        {item.summary}
                      </p>
                    )}
                  </div>
                  <ExternalLink className="w-4 h-4 text-gray-400 group-hover:text-[#000091] flex-shrink-0 mt-1" />
                </div>
              </a>
            ))}

            {items.length === 0 && !loading && (
              <div className="text-center py-12 text-gray-500">
                Aucun article trouvé pour ces critères
              </div>
            )}
          </div>
        )}

        {/* Sources Summary */}
        {sources && (
          <div className="mt-8 bg-white rounded-lg border border-gray-200 p-6">
            <h3 className="font-semibold text-gray-900 mb-4">Sources surveillées</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 text-sm">
              {Object.entries(sources.categories || {}).map(([key, label]) => {
                const count = sources.sources_by_category?.[key]?.length || 0;
                return (
                  <div key={key} className="text-center p-3 bg-gray-50 rounded-lg">
                    <div className="font-semibold text-[#000091]">{count}</div>
                    <div className="text-gray-500 text-xs">{label}</div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default VeillePage;
